import React, { useEffect, useRef, useState } from "react";

import { KeyRound, Loader2, Mail, Smartphone } from "lucide-react";

import type { BdcResendMethod } from "../../../../api/bdc.api";
import { cn } from "../../../../lib/utils";
import { Button } from "../../../ui/button";

/**
 * BdcPinConfirm — port de `mb-app-express/.../ExpressReport/PinConfirm.tsx`.
 *
 * Presentacional/controlado: el orquestador (F3) es dueño de la lógica de
 * mutaciones (confirm-pin / resend-pin) y de los toasts de error mapeados con
 * el catálogo de F1 (`mapBdcAxiosError`). Este componente sólo:
 *  - captura el NIP + su confirmación (2 grupos de 4 inputs),
 *  - valida localmente `pin === confirmPin` ("Los NIP no coinciden"),
 *  - expone botones "Reenviar NIP via Email/SMS" con contador `resendPin`,
 *  - deshabilita reenvío cuando `resendPin === 0`,
 *  - deshabilita "Confirmar NIP" cuando `retryPin === 0` y muestra un CTA,
 *  - incluye el disclaimer LRSIC art. 28 (texto informativo).
 *
 * El servidor informa los contadores restantes; el orquestador los refresca.
 */

const DISCLAIMER_COMPANY = "Multiburó SAPI de C.V.";

export interface BdcPinConfirmProps {
  /** Correo enmascarado al que se envió el NIP (mostrado en la descripción). */
  maskedEmail?: string;
  /** Intentos restantes de confirmación: `10 - pin_retry_attempts`. */
  retryPin: number;
  /** Reenvíos restantes: `5 - pin_resent_attempts`. */
  resendPin: number;
  /** Mutación en curso (confirm o resend) — deshabilita inputs y botones. */
  isLoading?: boolean;
  /** Confirma el NIP. El orquestador ejecuta `bdc/confirm-pin`. */
  onConfirm: (pin: string, confirmPin: string) => void;
  /** Reenvía el NIP por el método elegido. El orquestador ejecuta `bdc/resend-pin`. */
  onResend: (method: BdcResendMethod) => void;
}

const DIGITS = [0, 1, 2, 3];
const EMPTY_DIGITS = ["", "", "", ""];

export const BdcPinConfirm: React.FC<BdcPinConfirmProps> = ({
  maskedEmail,
  retryPin,
  resendPin,
  isLoading = false,
  onConfirm,
  onResend,
}) => {
  const [pinDigits, setPinDigits] = useState<string[]>(() => [...EMPTY_DIGITS]);
  const [confirmPinDigits, setConfirmPinDigits] = useState<string[]>(() => [
    ...EMPTY_DIGITS,
  ]);
  const [error, setError] = useState<string>();
  const [confirmError, setConfirmError] = useState<string>();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const confirmInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const pin = pinDigits.join("");
  const confirmPin = confirmPinDigits.join("");

  const retryExhausted = retryPin <= 0;
  const confirmDisabled = isLoading || retryExhausted;
  const resendDisabled = isLoading || resendPin <= 0;

  useEffect(() => {
    if (isLoading) return;
    inputRefs.current[0]?.focus();
  }, [isLoading]);

  const applyPinDigits = (value: string) => {
    const digits = value.replace(/[^0-9]/g, "").slice(0, 4);
    const newDigits = [...EMPTY_DIGITS];
    for (let i = 0; i < digits.length; i++) newDigits[i] = digits[i];
    setPinDigits(newDigits);
    if (error) setError(undefined);
    if (confirmError) setConfirmError(undefined);

    if (digits.length === 4) {
      confirmInputRefs.current[0]?.focus();
    } else {
      inputRefs.current[Math.min(digits.length, 3)]?.focus();
    }
  };

  const applyConfirmPinDigits = (value: string) => {
    const digits = value.replace(/[^0-9]/g, "").slice(0, 4);
    const newDigits = [...EMPTY_DIGITS];
    for (let i = 0; i < digits.length; i++) newDigits[i] = digits[i];
    setConfirmPinDigits(newDigits);
    if (confirmError) setConfirmError(undefined);

    confirmInputRefs.current[Math.min(digits.length, 3)]?.focus();
  };

  const handleDigitChange = (index: number, value: string) => {
    if (value.length > 1) {
      applyPinDigits(value);
      return;
    }

    const digit = value.replace(/[^0-9]/g, "").slice(-1);
    const newDigits = [...pinDigits];
    newDigits[index] = digit;
    setPinDigits(newDigits);
    if (error) setError(undefined);
    if (confirmError) setConfirmError(undefined);

    if (digit && index < 3) {
      inputRefs.current[index + 1]?.focus();
    } else if (digit && index === 3) {
      confirmInputRefs.current[0]?.focus();
    }
  };

  const handleConfirmDigitChange = (index: number, value: string) => {
    if (value.length > 1) {
      applyConfirmPinDigits(value);
      return;
    }

    const digit = value.replace(/[^0-9]/g, "").slice(-1);
    const newDigits = [...confirmPinDigits];
    newDigits[index] = digit;
    setConfirmPinDigits(newDigits);
    if (confirmError) setConfirmError(undefined);

    if (digit && index < 3) {
      confirmInputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !pinDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleConfirmKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !confirmPinDigits[index]) {
      if (index > 0) {
        confirmInputRefs.current[index - 1]?.focus();
      } else {
        inputRefs.current[3]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    applyPinDigits(e.clipboardData.getData("text"));
  };

  const handleConfirmPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    applyConfirmPinDigits(e.clipboardData.getData("text"));
  };

  const validateForm = (): boolean => {
    let isValid = true;

    if (!pin) {
      setError("Por favor introduzca su NIP");
      isValid = false;
    } else if (pin.length !== 4) {
      setError("El NIP debe tener 4 dígitos");
      isValid = false;
    }

    if (!confirmPin) {
      setConfirmError("Por favor confirme su NIP");
      isValid = false;
    } else if (confirmPin.length !== 4) {
      setConfirmError("La confirmación debe tener 4 dígitos");
      isValid = false;
    } else if (pin && confirmPin && pin !== confirmPin) {
      setConfirmError("Los NIP no coinciden");
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = () => {
    if (confirmDisabled) return;
    if (!validateForm()) return;
    onConfirm(pin, confirmPin);
  };

  return (
    <div id="bdc-pin-confirm" className="space-y-5">
      {/* Título y descripción */}
      <div className="space-y-2 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-50">
          <KeyRound className="h-8 w-8 text-[#28397a]" strokeWidth={1.5} />
        </div>
        <h2 className="text-xl font-semibold text-slate-900">Confirmar NIP</h2>
        <p className="mx-auto max-w-md text-sm text-slate-500">
          Introduce el Número de Identificación Personal (NIP) de 4 dígitos que
          te enviamos
          {maskedEmail ? (
            <>
              {" "}
              a{" "}
              <span className="font-medium text-slate-900">{maskedEmail}</span>
            </>
          ) : null}
          .
        </p>
      </div>

      {/* Inputs del NIP */}
      <div className="mx-auto max-w-xs space-y-4">
        <div className="space-y-2">
          <label
            htmlFor="bdc-pin-digit-1"
            className="block text-center text-xs font-medium uppercase tracking-wide text-slate-500"
          >
            NIP
          </label>
          <div className="flex justify-center gap-3" onPaste={handlePaste}>
            {DIGITS.map((index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                id={`bdc-pin-digit-${index + 1}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                aria-label={`NIP dígito ${index + 1}`}
                value={pinDigits[index]}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={isLoading}
                className={cn(
                  "h-14 w-12 rounded-xl border-2 bg-white text-center text-xl font-semibold text-slate-900 outline-none transition-all",
                  "focus:border-[#28397a] focus:ring-2 focus:ring-[#28397a]/20",
                  pinDigits[index] ? "border-slate-300" : "border-slate-200",
                  error &&
                    "border-red-400 focus:border-red-500 focus:ring-red-500/20",
                )}
                autoComplete={index === 0 ? "one-time-code" : "off"}
              />
            ))}
          </div>
          {error && <p className="text-center text-xs text-red-600">{error}</p>}
        </div>

        <div className="space-y-2">
          <label
            htmlFor="bdc-confirm-pin-digit-1"
            className="block text-center text-xs font-medium uppercase tracking-wide text-slate-500"
          >
            Confirmar NIP
          </label>
          <div
            className="flex justify-center gap-3"
            onPaste={handleConfirmPaste}
          >
            {DIGITS.map((index) => (
              <input
                key={`confirm-${index}`}
                ref={(el) => {
                  confirmInputRefs.current[index] = el;
                }}
                id={`bdc-confirm-pin-digit-${index + 1}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                aria-label={`Confirmar NIP dígito ${index + 1}`}
                value={confirmPinDigits[index]}
                onChange={(e) =>
                  handleConfirmDigitChange(index, e.target.value)
                }
                onKeyDown={(e) => handleConfirmKeyDown(index, e)}
                disabled={isLoading}
                className={cn(
                  "h-14 w-12 rounded-xl border-2 bg-white text-center text-xl font-semibold text-slate-900 outline-none transition-all",
                  "focus:border-[#28397a] focus:ring-2 focus:ring-[#28397a]/20",
                  confirmPinDigits[index]
                    ? "border-slate-300"
                    : "border-slate-200",
                  confirmError &&
                    "border-red-400 focus:border-red-500 focus:ring-red-500/20",
                )}
                autoComplete="off"
              />
            ))}
          </div>
          {confirmError && (
            <p className="text-center text-xs text-red-600">{confirmError}</p>
          )}
        </div>
      </div>

      {/* Confirmar NIP */}
      <div className="mx-auto max-w-sm space-y-2">
        <Button
          onClick={handleSubmit}
          disabled={confirmDisabled}
          className="h-12 w-full gap-2 text-base bg-[#28397a] hover:bg-[#1e2d5f]"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Confirmando...
            </>
          ) : (
            "Confirmar NIP"
          )}
        </Button>
        {retryExhausted && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center text-xs text-amber-800">
            Has agotado tus intentos para confirmar el NIP. Por favor
            contacta al equipo de soporte para continuar.
          </div>
        )}
      </div>

      {/* Reenvío — junto a la acción principal, antes del texto legal */}
      <div className="mx-auto max-w-sm space-y-2 text-center">
        <p className="text-xs text-slate-400">¿No recibiste tu NIP?</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            size="sm"
            disabled={resendDisabled}
            onClick={() => onResend("Email")}
            className="flex-1 justify-center border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-[#28397a]"
          >
            <Mail className="mr-2 h-4 w-4" />
            Reenviar NIP vía correo ({resendPin})
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={resendDisabled}
            onClick={() => onResend("SMS")}
            className="flex-1 justify-center border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-[#28397a]"
          >
            <Smartphone className="mr-2 h-4 w-4" />
            Reenviar NIP vía SMS ({resendPin})
          </Button>
        </div>
        {resendPin <= 0 && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            Agotaste los reenvíos disponibles. Usa el NIP más reciente; si no lo
            recibiste, contacta al equipo de soporte para continuar.
          </p>
        )}
      </div>

      {/* Disclaimer LRSIC art. 28 */}
      <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
        <p className="text-xs leading-relaxed text-slate-500">
          Por este conducto autorizo expresamente a{" "}
          <span className="font-semibold">{DISCLAIMER_COMPANY}</span>, para que
          por conducto de sus funcionarios facultados lleve a cabo
          Investigaciones, sobre mi comportamiento Crediticio en las Sociedades
          de Información Crediticia que estime conveniente. Así mismo, declaro
          que conozco la naturaleza y alcance de la información que se
          solicitará, del uso que{" "}
          <span className="font-semibold">{DISCLAIMER_COMPANY}</span> hará de
          tal información y de que ésta podrá realizar consultas periódicas de
          mi historial crediticio, consintiendo que esta autorización se
          encuentre vigente por un periodo de 3 años contados a partir de la
          fecha de su expedición y en todo caso durante el tiempo que
          mantengamos relación jurídica. Acepto que este documento quede bajo
          propiedad de{" "}
          <span className="font-semibold">{DISCLAIMER_COMPANY}</span> y/o en las
          sociedades de información crediticia consultadas para efectos de
          control y cumplimiento del artículo 28 de la LRSIC.
        </p>
      </div>
    </div>
  );
};

export default BdcPinConfirm;
